import React from 'react';
import { Bot, CheckCircle2, FileSearch, Files, Network, ShieldCheck, Sparkles } from 'lucide-react';
import ProductPage, { ProductSection } from '../components/ProductPage';
import { useTranslations } from '../context/TranslationContext';
import usePageMetadata from '../hooks/usePageMetadata';

const COPY = {
  it: {
    eyebrow: 'Knowledge graph · RAG',
    title: 'Trasforma i tuoi documenti in conoscenza connessa.',
    description: 'Doc2Graph trasforma fonti non strutturate in knowledge graph privati per offrire agli agenti AI un retrieval più contestuale, preciso e verificabile.',
    contact: 'Contattaci', subject: 'Richiesta informazioni Doc2Graph',
    problemTitle: 'La conoscenza è dispersa',
    problem: 'Documenti, repository, cloud, email e pagine web contengono informazioni critiche, ma la ricerca tradizionale restituisce frammenti isolati e non le relazioni che li rendono utili.',
    flowTitle: 'Dalla fonte all’intelligenza',
    flowIntro: 'Una pipeline automatizzata converte contenuti eterogenei in una base di conoscenza interrogabile.',
    comparisonTitle: 'Il vantaggio della conoscenza connessa',
    comparisonIntro: 'Tre approcci a confronto: dal semplice accesso ai contenuti a un knowledge graph dinamico che conserva le relazioni tra le fonti.',
    comparisonCaption: 'Confronto tra provider AI, software legacy e LegisRatio',
    comparisonCriterion: 'Capacità',
    comparisonScrollHint: 'Scorri orizzontalmente per confrontare gli approcci →',
    benefitsTitle: 'Un retrieval migliore per persone e agenti',
    benefitsIntro: 'Più contesto, relazioni verificabili e risposte fondate sulle tue fonti.',
    provider: 'Compatibile con i principali provider AI: nessun vincolo su un singolo modello.',
  },
  en: {
    eyebrow: 'Knowledge graph · RAG',
    title: 'Turn your documents into connected knowledge.',
    description: 'Doc2Graph transforms unstructured sources into private knowledge graphs, giving AI agents more contextual, precise and verifiable retrieval.',
    contact: 'Contact us', subject: 'Doc2Graph information request',
    problemTitle: 'Knowledge is scattered',
    problem: 'Documents, repositories, cloud storage, email and web pages hold critical information, but traditional search returns isolated fragments instead of the relationships that make them useful.',
    flowTitle: 'From source to intelligence',
    flowIntro: 'An automated pipeline converts heterogeneous content into a queryable knowledge base.',
    comparisonTitle: 'The advantage of connected knowledge',
    comparisonIntro: 'Three approaches compared: from basic access to content to a dynamic knowledge graph that preserves relationships across sources.',
    comparisonCaption: 'Comparison of AI providers, legacy software and LegisRatio',
    comparisonCriterion: 'Capability',
    comparisonScrollHint: 'Scroll horizontally to compare approaches →',
    benefitsTitle: 'Better retrieval for people and agents',
    benefitsIntro: 'More context, verifiable relationships, and answers grounded in your sources.',
    provider: 'Compatible with leading AI providers, with no lock-in to a single model.',
  }
};

const Doc2GraphPage: React.FC = () => {
  const { currentLanguage, t } = useTranslations();
  const c = COPY[currentLanguage];
  usePageMetadata('Doc2Graph | LegisRatio', c.description);

  const steps = currentLanguage === 'it' ? [
    ['Connect', 'PDF, DOCX, pagine web e altre fonti non strutturate.', Files],
    ['Model', 'Tecnologia proprietaria estrae concetti, entità e relazioni.', FileSearch],
    ['Graph', 'Creazione automatica del knowledge graph privato.', Network],
    ['Empower', 'Gli agenti interrogano il grafo in linguaggio naturale.', Bot],
  ] : [
    ['Connect', 'PDFs, DOCX, web pages and other unstructured sources.', Files],
    ['Model', 'Proprietary technology extracts concepts, entities and relationships.', FileSearch],
    ['Graph', 'Automatic creation of a private knowledge graph.', Network],
    ['Empower', 'Agents query the graph using natural language.', Bot],
  ];
  const benefits = currentLanguage === 'it' ? [
    ['Relazioni, non solo chunk', 'Il grafo conserva i collegamenti tra informazioni distribuite in fonti diverse.', Network],
    ['Risposte verificabili', 'Il retrieval mantiene il collegamento ai documenti che sostengono ogni risposta.', ShieldCheck],
    ['Contesto per il RAG', 'Gli agenti ricevono una struttura più ricca rispetto a frammenti testuali isolati.', Sparkles],
  ] : [
    ['Relationships, not just chunks', 'The graph preserves links across information distributed in different sources.', Network],
    ['Verifiable answers', 'Retrieval keeps the connection to documents supporting each answer.', ShieldCheck],
    ['Context for RAG', 'Agents receive richer structure than isolated text fragments.', Sparkles],
  ];
  const comparisonRows = currentLanguage === 'it' ? [
    {
      label: 'Modello dati',
      cells: [
        { text: 'Nessun modello — accesso al web' },
        { text: 'Database documentale + chunk' },
        { text: 'Knowledge graph dinamico', supported: true },
      ],
    },
    {
      label: 'Query in linguaggio naturale',
      cells: [
        { text: 'Supportata', supported: true },
        { text: 'Limitata' },
        { text: 'Supportata', supported: true },
      ],
    },
    {
      label: 'Analisi tra documenti',
      cells: [
        { text: 'Limitata al contesto' },
        { text: 'Limitata, senza interconnessioni' },
        { text: 'Supportata', supported: true },
      ],
    },
  ] : [
    {
      label: 'Data model',
      cells: [
        { text: 'No model — access to the web' },
        { text: 'Document database + chunks' },
        { text: 'Dynamic knowledge graph', supported: true },
      ],
    },
    {
      label: 'Natural-language query',
      cells: [
        { text: 'Supported', supported: true },
        { text: 'Limited' },
        { text: 'Supported', supported: true },
      ],
    },
    {
      label: 'Cross-document analysis',
      cells: [
        { text: 'Limited to context' },
        { text: 'Limited, with no interconnections' },
        { text: 'Supported', supported: true },
      ],
    },
  ];

  return (
    <ProductPage eyebrow={c.eyebrow} title={c.title} description={c.description} contactLabel={c.contact} contactSubject={c.subject}>
      <ProductSection title={c.problemTitle} intro={c.problem}>
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
          {['PDF', 'DOCX', 'Web', 'Cloud', 'Email'].map(source => <div key={source} className="glass-panel rounded-xl p-5 text-center font-semibold text-gray-200">{source}</div>)}
        </div>
      </ProductSection>
      <ProductSection title={c.flowTitle} intro={c.flowIntro}>
        <div className="relative mt-12 md:mt-16">
          {/* Progressive horizontal line (flat line, no glow) */}
          <div
            aria-hidden="true"
            className="absolute left-[12.5%] right-[12.5%] top-7 hidden h-0.5 bg-gradient-to-r from-cosmic-teal via-mystic-violet to-indigo-400 md:block"
          />

          <ol className="relative grid gap-8 md:grid-cols-4 md:gap-0">
            {steps.map(([title,text,Icon], index) => (
              <li key={title as string} className="group relative grid grid-cols-[3.5rem_minmax(0,1fr)] gap-4 md:block md:px-5 md:text-center">
                {/* Vertical connecting line for mobile */}
                {index < steps.length - 1 && (
                  <span aria-hidden="true" className="absolute left-7 top-14 h-[calc(100%+2rem)] w-0.5 bg-gradient-to-b from-cosmic-teal via-mystic-violet to-indigo-400 md:hidden" />
                )}
                <div className="relative z-10 flex h-14 w-14 items-center justify-center rounded-2xl border border-cosmic-teal/40 bg-void-blue text-cosmic-teal transition-colors duration-300 group-hover:border-cosmic-teal md:mx-auto">
                  <Icon size={27} strokeWidth={1.8}/>
                </div>
                <div className="pt-0.5 md:mt-5 md:pt-0">
                  <h3 className="text-xl font-bold">{title as string}</h3>
                  <p className="mt-2.5 text-base leading-relaxed text-gray-300">{text as string}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </ProductSection>
      <ProductSection title={c.comparisonTitle} intro={c.comparisonIntro} className="bg-white/[0.015]">
        <p className="mb-4 text-sm text-gray-400 md:hidden">{c.comparisonScrollHint}</p>
        <div className="overflow-hidden rounded-2xl border border-white/10 bg-deep-space/50 shadow-2xl backdrop-blur-sm">
          <div className="overflow-x-auto" tabIndex={0} aria-label={c.comparisonCaption}>
            <table className="w-full min-w-[860px] table-fixed border-collapse text-left">
              <caption className="sr-only">{c.comparisonCaption}</caption>
              <thead>
                <tr className="border-b border-white/10">
                  {/* Legend Header Column */}
                  <th scope="col" className="sticky left-0 z-20 w-[28%] bg-deep-space/90 backdrop-blur-md px-6 py-6 border-r border-white/10">
                    <span className="sr-only">{c.comparisonCriterion}</span>
                  </th>
                  {/* AI Providers */}
                  <th scope="col" className="w-[24%] border-l border-white/10 px-6 py-6 text-lg font-bold text-gray-200">
                    {t('doc2graph_ai_providers')}
                  </th>
                  {/* Legacy Software */}
                  <th scope="col" className="w-[24%] border-l border-white/10 px-6 py-6 text-lg font-bold text-gray-200">
                    {t('doc2graph_legacy_software')}
                  </th>
                  {/* LegisRatio Column */}
                  <th scope="col" className="w-[24%] border-l border-white/10 bg-indigo-500/[0.14] px-6 py-6 text-lg font-bold text-indigo-300">
                    LegisRatio
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {comparisonRows.map((row) => (
                  <tr key={row.label} className="transition-colors hover:bg-white/[0.02]">
                    {/* Legend Row Label Column - Soft transparent background */}
                    <th scope="row" className="sticky left-0 z-20 bg-white/[0.035] backdrop-blur-md px-6 py-6 text-base font-semibold text-gray-200 border-r border-white/10">
                      {row.label}
                    </th>
                    {/* AI Providers */}
                    <td className="border-l border-white/10 px-6 py-6 leading-relaxed text-gray-300">
                      <span className="flex items-start gap-2.5">
                        {row.cells[0].supported && <CheckCircle2 aria-hidden="true" className="mt-0.5 shrink-0 text-cosmic-teal" size={19} />}
                        <span>{row.cells[0].text}</span>
                      </span>
                    </td>
                    {/* Legacy Software */}
                    <td className="border-l border-white/10 px-6 py-6 leading-relaxed text-gray-300">
                      <span className="flex items-start gap-2.5">
                        {row.cells[1].supported && <CheckCircle2 aria-hidden="true" className="mt-0.5 shrink-0 text-cosmic-teal" size={19} />}
                        <span>{row.cells[1].text}</span>
                      </span>
                    </td>
                    {/* LegisRatio Column */}
                    <td className="border-l border-white/10 bg-indigo-500/[0.1] px-6 py-6 leading-relaxed text-white font-medium">
                      <span className="flex items-start gap-2.5">
                        <CheckCircle2 aria-hidden="true" className="mt-0.5 shrink-0 text-indigo-300" size={19} />
                        <span className="font-semibold text-white">{row.cells[2].text}</span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </ProductSection>
      <ProductSection title={c.benefitsTitle} intro={c.benefitsIntro}>
        <div className="grid gap-6 md:grid-cols-3">
          {benefits.map(([title,text,Icon]) => <div key={title as string} className="rounded-2xl border border-white/10 p-7"><Icon className="mb-5 text-nebula-pink" size={32}/><h3 className="text-xl font-bold">{title as string}</h3><p className="mt-3 leading-relaxed text-gray-400">{text as string}</p></div>)}
        </div>
        <p className="mt-10 rounded-2xl bg-white/[0.04] p-6 text-center text-lg text-gray-300">{c.provider}</p>
      </ProductSection>
    </ProductPage>
  );
};

export default Doc2GraphPage;
